// app/utils/connectors-config.js
const connectors = [
  {
    id: 'github',
    name: 'GitHub',
    link: 'https://github.com/apps/test-repo-analysis-indro',
    category: 'Version Control',
    description:
      'Analyze repositories, pull requests, contributor activity, and CI/CD pipelines from GitHub organizations.',
    longDescription:
      'Connect your GitHub organization to unlock deep visibility into your engineering operations. Scaleflow analyzes repository health, code review patterns, contributor velocity, merge workflows, and CI/CD pipeline reliability — giving you a comprehensive view of technical maturity and risk for due diligence.',
    connectLabel: 'Connect GitHub',
    enabled: true,
    features: [
      {
        title: 'Code Quality Analysis',
        description:
          'Assess code health through PR review coverage, merge conflict frequency, and branch hygiene across all repositories.',
      },
      {
        title: 'PR & Review Patterns',
        description:
          'Evaluate review turnaround times, approval workflows, and bottleneck detection in the pull request lifecycle.',
      },
      {
        title: 'Team Velocity Metrics',
        description:
          'Track commit frequency, deployment cadence, and sprint throughput per team and contributor.',
      },
      {
        title: 'Risk Indicators',
        description:
          'Identify single points of failure, bus factor risks, stale repositories, and dependency vulnerabilities.',
      },
      {
        title: 'CI/CD Pipeline Health',
        description:
          'Monitor build success rates, deployment frequency, and pipeline reliability across GitHub Actions workflows.',
      },
      {
        title: 'Contributor Activity',
        description:
          'Map active contributors, collaboration patterns, and knowledge distribution across the codebase.',
      },
    ],
  },
  {
    id: 'github-public',
    name: 'GitHub Public Repos',
    category: 'Public Access',
    description:
      'Browse and analyze public GitHub repositories without authentication. Perfect for open-source due diligence.',
    longDescription:
      'Access public GitHub repositories without any authentication. Evaluate open-source projects, assess contributor patterns, and measure technical maturity across public repositories for comprehensive due diligence.',
    connectLabel: 'Connect GitHub Public',
    enabled: true,
    requiresIntegration: false,
    features: [
      {
        title: 'Open Source Health',
        description:
          'Evaluate maintenance activity, issue resolution rates, and community engagement across public repositories.',
      },
      {
        title: 'Contributor Patterns',
        description:
          'Analyse contributor diversity, bus factor, and knowledge concentration in open source projects.',
      },
      {
        title: 'Code Activity',
        description:
          'Track commit frequency, release cadence, and development velocity over time.',
      },
      {
        title: 'Repository Quality',
        description:
          'Assess documentation coverage, test presence, and code organisation across repositories.',
      },
      {
        title: 'Dependency Analysis',
        description:
          'Identify outdated or vulnerable dependencies across public repositories.',
      },
      {
        title: 'Fork & Star Trends',
        description:
          'Understand community adoption and reputation signals from GitHub activity metrics.',
      },
    ],
  },
  {
    id: 'gitlab',
    name: 'GitLab',
    category: 'Version Control',
    description:
      'Scan GitLab groups for merge request workflows, pipeline performance, and engineering team dynamics.',
    longDescription:
      'Connect your GitLab instance to gain comprehensive insight into your engineering operations. Scaleflow analyzes merge request patterns, pipeline reliability, and contributor activity to surface technical maturity signals.',
    connectLabel: 'Connect GitLab',
    enabled: true,
    features: [
      {
        title: 'Merge Request Analysis',
        description:
          'Evaluate merge request workflows, approval patterns, and code review quality across GitLab groups.',
      },
      {
        title: 'Pipeline Performance',
        description:
          'Monitor CI/CD pipeline success rates, duration trends, and failure patterns.',
      },
      {
        title: 'Team Dynamics',
        description:
          'Track contribution patterns, team collaboration, and workload distribution.',
      },
      {
        title: 'Repository Health',
        description:
          'Assess branch hygiene, merge frequency, and repository maintenance across your GitLab instance.',
      },
      {
        title: 'Issue Tracking',
        description:
          'Analyse issue resolution times, backlog health, and delivery predictability.',
      },
      {
        title: 'Security Scanning',
        description:
          "Review security findings from GitLab's built-in SAST and dependency scanning tools.",
      },
    ],
  },
  {
    id: 'azure-devops',
    name: 'Azure DevOps',
    category: 'Version Control',
    description:
      'Evaluate Azure Repos, Boards, and Pipelines for engineering maturity and delivery risk signals.',
    longDescription:
      'Connect your Azure DevOps organisation to analyse engineering delivery health. Scaleflow reviews pipeline reliability, work item flow, pull request patterns, and deployment frequency to provide a complete picture of technical operations.',
    connectLabel: 'Connect Azure DevOps',
    enabled: false,
    features: [
      {
        title: 'Work Item Analysis',
        description:
          'Evaluate work item flow, sprint velocity, and delivery predictability across Azure Boards.',
      },
      {
        title: 'Pipeline Health',
        description:
          'Monitor build and release pipeline reliability, duration, and failure rates across Azure Pipelines.',
      },
      {
        title: 'Repository Metrics',
        description:
          'Track pull request workflows, branch policies, and code review quality in Azure Repos.',
      },
      {
        title: 'Team Performance',
        description:
          'Analyse team velocity, capacity utilisation, and throughput trends over time.',
      },
      {
        title: 'Deployment Frequency',
        description:
          'Track deployment cadence, lead time, and change failure rates across environments.',
      },
      {
        title: 'Test Coverage',
        description:
          'Evaluate automated test coverage, test pass rates, and quality gate adherence.',
      },
    ],
  },
  {
    id: 'manual',
    name: 'Manual Upload',
    category: 'Manual',
    description:
      'Upload repository archives directly — no connector setup required.',
    longDescription:
      'For organisations that cannot install a version control connector for security reasons. Upload .tar.gz archives of your repositories here — one per repository — and reuse them across any number of X-Rays. No OAuth or API keys needed.',
    connectLabel: 'Manual Upload',
    enabled: true,
    requiresIntegration: false,
    features: [],
  },
];

export type ConnectorType = (typeof connectors)[0];
export default connectors;

export function connectorById(id: string): ConnectorType {
  const connector = connectors.find((c) => c.id === id);
  if (!connector) throw new Error(`Unknown connector id: ${id}`);
  return connector;
}
